import { recurringNextDate } from '@/function/recurring/recurring.period';
import type { TypeRecurring } from '@/types/recurring/types.recurring';
import type { TypeRecurringBilling } from '@/types/recurring/types.recurring.calendar';
import { isDayKey } from '@/utils/date/day.key';

const WALK_MAX = 400;

const within = (date: string, from: string, to: string): boolean => isDayKey(date) && date >= from && date < to;

export function recurringCalendarBillings(plans: readonly TypeRecurring[], from: string, to: string): TypeRecurringBilling[] {
    if (!isDayKey(from) || !isDayKey(to) || from >= to) return [];
    const billings: TypeRecurringBilling[] = [];
    const seen = new Set<string>();

    for (const plan of plans) {
        if (seen.has(plan.id)) continue;
        seen.add(plan.id);
        const taken = new Set<string>();

        for (const run of plan.runs) {
            if (!within(run.date, from, to) || taken.has(run.date)) continue;
            taken.add(run.date);
            billings.push({ key: `${plan.id}@${run.date}`, plan, date: run.date, kind: run.outcome, invoiceId: run.invoiceId || null });
        }

        if (plan.status !== 'active' || !isDayKey(plan.nextDate)) continue;
        let date = plan.nextDate;
        for (let step = 0; date && date < to && step < WALK_MAX; step += 1) {
            if (isDayKey(plan.endDate) && date > plan.endDate) break;
            if (date >= from && !taken.has(date)) {
                taken.add(date);
                billings.push({ key: `${plan.id}@${date}`, plan, date, kind: 'planned', invoiceId: null });
            }
            date = recurringNextDate(date, plan.every, plan.anchorDay);
        }
    }

    return billings.sort((a, b) => a.date.localeCompare(b.date) || a.plan.name.localeCompare(b.plan.name, 'it') || a.key.localeCompare(b.key));
}

export default recurringCalendarBillings;
